import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-capture-scriptsure-patient-detail-dialog',
  templateUrl: './capture-scriptsure-patient-detail-dialog.component.html',
  styleUrls: ['./capture-scriptsure-patient-detail-dialog.component.css']
})
export class CaptureScriptSurePatientDetailDialogComponent {
  patientForm: FormGroup;
  showNextButton: boolean = false;
  currentStep: number = 1;
  totalSteps: number = 2;

  states = [
    { name: 'Alabama', abbreviation: 'AL' },
    { name: 'Alaska', abbreviation: 'AK' },
    { name: 'American Samoa', abbreviation: 'AS' },
    { name: 'Arizona', abbreviation: 'AZ' },
    { name: 'Arkansas', abbreviation: 'AR' },
    { name: 'California', abbreviation: 'CA' },
    { name: 'Colorado', abbreviation: 'CO' },
    { name: 'Connecticut', abbreviation: 'CT' },
    { name: 'Delaware', abbreviation: 'DE' },
    { name: 'District Of Columbia', abbreviation: 'DC' },
    { name: 'Federated States Of Micronesia', abbreviation: 'FM' },
    { name: 'Florida', abbreviation: 'FL' },
    { name: 'Georgia', abbreviation: 'GA' },
    { name: 'Guam', abbreviation: 'GU' },
    { name: 'Hawaii', abbreviation: 'HI' },
    { name: 'Idaho', abbreviation: 'ID' },
    { name: 'Illinois', abbreviation: 'IL' },
    { name: 'Indiana', abbreviation: 'IN' },
    { name: 'Iowa', abbreviation: 'IA' },
    { name: 'Kansas', abbreviation: 'KS' },
    { name: 'Kentucky', abbreviation: 'KY' },
    { name: 'Louisiana', abbreviation: 'LA' },
    { name: 'Maine', abbreviation: 'ME' },
    { name: 'Marshall Islands', abbreviation: 'MH' },
    { name: 'Maryland', abbreviation: 'MD' },
    { name: 'Massachusetts', abbreviation: 'MA' },
    { name: 'Michigan', abbreviation: 'MI' },
    { name: 'Minnesota', abbreviation: 'MN' },
    { name: 'Mississippi', abbreviation: 'MS' },
    { name: 'Missouri', abbreviation: 'MO' },
    { name: 'Montana', abbreviation: 'MT' },
    { name: 'Nebraska', abbreviation: 'NE' },
    { name: 'Nevada', abbreviation: 'NV' },
    { name: 'New Hampshire', abbreviation: 'NH' },
    { name: 'New Jersey', abbreviation: 'NJ' },
    { name: 'New Mexico', abbreviation: 'NM' },
    { name: 'New York', abbreviation: 'NY' },
    { name: 'North Carolina', abbreviation: 'NC' },
    { name: 'North Dakota', abbreviation: 'ND' },
    { name: 'Northern Mariana Islands', abbreviation: 'MP' },
    { name: 'Ohio', abbreviation: 'OH' },
    { name: 'Oklahoma', abbreviation: 'OK' },
    { name: 'Oregon', abbreviation: 'OR' },
    { name: 'Palau', abbreviation: 'PW' },
    { name: 'Pennsylvania', abbreviation: 'PA' },
    { name: 'Puerto Rico', abbreviation: 'PR' },
    { name: 'Rhode Island', abbreviation: 'RI' },
    { name: 'South Carolina', abbreviation: 'SC' },
    { name: 'South Dakota', abbreviation: 'SD' },
    { name: 'Tennessee', abbreviation: 'TN' },
    { name: 'Texas', abbreviation: 'TX' },
    { name: 'Utah', abbreviation: 'UT' },
    { name: 'Vermont', abbreviation: 'VT' },
    { name: 'Virgin Islands', abbreviation: 'VI' },
    { name: 'Virginia', abbreviation: 'VA' },
    { name: 'Washington', abbreviation: 'WA' },
    { name: 'West Virginia', abbreviation: 'WV' },
    { name: 'Wisconsin', abbreviation: 'WI' },
    { name: 'Wyoming', abbreviation: 'WY' }
  ];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<CaptureScriptSurePatientDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { patient: any, facility: any }
  ) {    
    this.patientForm = this.fb.group({
      prescriptionAddress: ['patient', Validators.required],
      address: [data.patient.address || '', Validators.required],
      city: [data.patient.city || '', Validators.required],
      state: [data.patient.state || '', Validators.required],
      zipCode: [data.patient.zipCode || '', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]],
      phoneNumber: [data.patient.phoneNumber || '', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      firstName: [data.patient.first_name || '', Validators.required],
      lastName: [data.patient.last_name || '', Validators.required],
      dateOfBirth: [data.patient.date_of_birth || '', Validators.required],
      gender: [data.patient.gender || '', Validators.required]
    });

    // Check if patient has missing required information
    this.showNextButton = this.hasMissingPatientInfo();

    // Subscribe to prescription address changes
    this.patientForm.get('prescriptionAddress').valueChanges.subscribe(value => {
      this.updateAddress(value);
    });
  }

  hasMissingPatientInfo(): boolean {
    const requiredFields = ['first_name', 'last_name', 'date_of_birth', 'gender'];
    const missingFields: boolean = requiredFields.some(field => !this.data.patient[field]);
    return missingFields;
  }

  updateAddress(type: string) {
    if (type === 'patient') {
      this.patientForm.patchValue({
        address: this.data.patient.address || '',
        city: this.data.patient.city || '',
        state: this.data.patient.state || '',
        zipCode: this.data.patient.zip_code || '',
        phoneNumber: this.data.patient.phone || ''
      });
    } else if (type === 'facility') {
      this.patientForm.patchValue({
        address: this.data.facility.address || '',
        city: this.data.facility.city || '',
        state: this.data.facility.state || '',
        zipCode: this.data.facility.zip_code || '',
        phoneNumber: this.data.facility.phoneNumber || ''
      });
    }
  }

  onPatientAddressClick() {
    this.patientForm.patchValue({
      address: this.data.patient.address || '',
      city: this.data.patient.city || '',
      state: this.data.patient.state || '',
      zipCode: this.data.patient.zipCode || '',
      phoneNumber: this.data.patient.phoneNumber || ''
    });
  }

  onFacilityAddressClick() {
    this.patientForm.patchValue({
      address: this.data.facility.address || '',
      city: this.data.facility.city || '',
      state: this.data.facility.state || '',
      zipCode: this.data.facility.zipCode || '',
      phoneNumber: this.data.facility.phoneNumber || ''
    });
  }

  onSubmit() {
    if (this.isCurrentStepValid()) {
      if (this.showNextButton && this.currentStep === 1) {
        this.currentStep++;
      } else {
        // Combine all form values and close dialog
        const formValue = this.patientForm.value;
        this.dialogRef.close(formValue);
      }
    }
  }

  onPrevious() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  isCurrentStepValid(): boolean {
    if (this.currentStep === 1) {
      const step1Fields = ['prescriptionAddress', 'address', 'city', 'state', 'zipCode', 'phoneNumber'];
      return step1Fields.every(field => {
        const control = this.patientForm.get(field);
        return control && control.valid;
      });
    } else {
      const step2Fields = ['firstName', 'lastName', 'dateOfBirth', 'gender'];
      return step2Fields.every(field => {
        const control = this.patientForm.get(field);
        return control && control.valid;
      });
    }
  }
} 